import { convertRemToPx } from '@disco/utils/dom'

// Layout Constants
const MOBILE_BREAKPOINT = 768
const MOBILE_HEIGHT_BREAKPOINT = 500

// Column Width Constants
const COLUMN_WIDTH_DEFAULT = 320
const COLUMN_WIDTH_LARGE = 380
const COLUMN_WIDTH_LARGER = 490

// Spacing Constants
const EXPLORER_RIGHT_SPACER = 10
const EXPLORER_INNER_MARGIN_LEFT = 16 // margin-left on Explorer_Inner container
const PANE_SPACING = 24 // 10px margin-right + 2px borders
const MINIMUM_PANE_WIDTH = 250
const MAXIMUM_PANE_WIDTH = 450
const SEARCH_MARGIN_REM = 24.5
const VIDEO_MARGIN_REM = 4
const VIDEO_MAX_WIDTH_REM = 60

// Aspect Ratios
const VIDEO_ASPECT_RATIO = 0.5625 // 16:9 aspect ratio (9/16)

/** Column width setting options for the explorer interface. */
export type ColumnSetting = 'Default' | 'Large' | 'Larger'

/**
 * Calculates the available width for the explorer interface.
 *
 * Determines how much horizontal space is available for the main explorer
 * content after accounting for sidebar and shortlist widths.
 *
 * @param windowWidth - Total window width in pixels
 * @param sidebarOpen - Whether the sidebar is currently open
 * @param shortlistsOpen - Whether the shortlists are open
 * @param launchedOnMobileDevice - Whether the app is running on a mobile device
 * @returns Available width for explorer content in pixels
 *
 * @example
 * ```typescript
 * const explorerWidth = getExplorerWidth(1200, true, true, false)
 * // Returns width after subtracting sidebar (240px) and shortlist (400px)
 *
 * const mobileWidth = getExplorerWidth(500, false, true, true)
 * // Returns width with collapsed sidebar (-56px) plus mobile padding (+16px)
 *
 * const tabletWidth = getExplorerWidth(800, false, true, true)
 * // Returns width with collapsed sidebar (-56px) for tablet
 * ```
 */

export function getPlaylistCreatorWidth(
  windowWidth,
  sidebarOpen,
  launchedOnMobileDevice,
  shortlistsOpen,
) {
  let explorerWidth = windowWidth
  const sidebarWidth = sidebarOpen ? 240 : 56
  const playlistCreatorMaxWidth = launchedOnMobileDevice ? 300 : 400

  if (!launchedOnMobileDevice) {
    explorerWidth -= sidebarWidth
    if (shortlistsOpen) explorerWidth -= playlistCreatorMaxWidth
  } else {
    explorerWidth -= 20
    if (sidebarOpen) explorerWidth -= 10
  }

  let mobileWidth = explorerWidth - 250
  // calc minWidth limit for mobile
  if (mobileWidth < 300) mobileWidth = 300

  let playlistCreatorWidth = 0

  const limitWidth = launchedOnMobileDevice
    ? mobileWidth
    : playlistCreatorMaxWidth

  if (shortlistsOpen) {
    playlistCreatorWidth = Math.min(explorerWidth, limitWidth)
  }
  if (!launchedOnMobileDevice && playlistCreatorWidth < 350)
    playlistCreatorWidth = 350

  if (launchedOnMobileDevice && playlistCreatorWidth < 300)
    playlistCreatorWidth = 300

  return playlistCreatorWidth
}

export function getExplorerWidth(
  windowWidth: number,
  sidebarOpen: boolean,
  shortlistsOpen: boolean,
  launchedOnMobileDevice: boolean,
  playlistCreatorWidth?: number,
): number {
  const sidebarWidth = sidebarOpen ? 240 : 56
  const shortlistWidth = playlistCreatorWidth ?? 400

  let explorerWidth = windowWidth

  if (!launchedOnMobileDevice) {
    // Always subtract sidebar width on desktop
    explorerWidth -= sidebarWidth
    // Subtract shortlist width when expanded on desktop
    if (shortlistsOpen) {
      explorerWidth -= shortlistWidth
    }
  } else {
    explorerWidth -= 20
    // Don't subtract shortlist width on mobile - it overlays
  }
  return explorerWidth
}

/**
 * Gets the base column width for different column settings.
 *
 * Returns the standard width for columns based on the user's
 * column size preference setting.
 *
 * @param columnSetting - The column size setting ('Default', 'Large', or 'Larger')
 * @returns Base column width in pixels
 *
 * @example
 * ```typescript
 * const defaultWidth = getBaseColumnWidth('Default') // 320
 * const largeWidth = getBaseColumnWidth('Large') // 380
 * const largerWidth = getBaseColumnWidth('Larger') // 450
 * ```
 */
export function getBaseColumnWidth(
  columnSetting: 'Default' | 'Large' | 'Larger',
): number {
  switch (columnSetting.toLowerCase()) {
    case 'default':
      return COLUMN_WIDTH_DEFAULT
    case 'large':
      return COLUMN_WIDTH_LARGE
    case 'larger':
      return COLUMN_WIDTH_LARGER
    default:
      return COLUMN_WIDTH_DEFAULT
  }
}

/**
 * Calculates how many columns can fit in the available explorer width.
 *
 * Determines the optimal number of columns that can be displayed
 * based on the available width and column size setting.
 *
 * @param explorerWidth - Available width for the explorer content
 * @param columnSetting - Column size setting (defaults to 'Default')
 * @returns Number of columns that can fit (minimum 1)
 *
 * @example
 * ```typescript
 * const colCount = getColCount(1000, 'Default') // 3 columns (1000 / 320)
 * const largeColCount = getColCount(1000, 'Large') // 2 columns (1000 / 380)
 * const narrowColCount = getColCount(200, 'Default') // 1 column (minimum)
 * ```
 */
export function getColCount(
  explorerWidth: number,
  columnSetting: ColumnSetting = 'Default',
): number {
  let colCount = Math.floor(explorerWidth / getBaseColumnWidth(columnSetting))
  colCount = colCount > 0 ? colCount : 1
  return colCount
}

/**
 * Calculates the optimal column count considering width constraints.
 *
 * This function ensures that the total width of all columns (including spacing)
 * never exceeds the available explorer width, preventing horizontal overflow.
 *
 * @param explorerWidth - Available width for the explorer content
 * @param columnSetting - Column size setting (defaults to 'Default')
 * @returns Number of columns that fit within width constraints (minimum 1)
 */
export function getConstrainedColCount(
  explorerWidth: number,
  columnSetting: ColumnSetting = 'Default',
): number {
  const baseWidth = getBaseColumnWidth(columnSetting)
  const availableWidthForColumns =
    explorerWidth - EXPLORER_RIGHT_SPACER - EXPLORER_INNER_MARGIN_LEFT

  // Calculate width needed per column when using maximum pane width
  const maxPaneWidthWithSpacing =
    MAXIMUM_PANE_WIDTH + PANE_SPACING + EXPLORER_RIGHT_SPACER

  // Initial column count based on base width
  let colCount = Math.floor(availableWidthForColumns / baseWidth)
  const colWidth = Math.min(baseWidth, maxPaneWidthWithSpacing)

  // Ensure we don't exceed available space with maximum width constraints
  const maxColumnsForMaxWidth = Math.floor(availableWidthForColumns / colWidth)

  // Use the most restrictive constraint
  colCount = Math.min(colCount, maxColumnsForMaxWidth)
  colCount = Math.max(colCount, 1) // Always have at least 1 column

  return colCount
}

/**
 * Calculates the actual width of explorer panes accounting for layout constraints.
 *
 * Determines the final width of explorer panes, handling both desktop and mobile
 * layouts. On mobile, columns are resized to fit the available space, while on
 * desktop they maintain their base width with proper spacing.
 *
 * @param windowWidth - Total window width in pixels
 * @param columnSetting - Column size setting (defaults to 'Default')
 * @param sidebarOpen - Whether the sidebar is currently open
 * @param launchedOnMobileDevice - Whether the app is running on a mobile device
 * @returns Actual pane width in pixels
 *
 * @example
 * ```typescript
 * // Desktop layout
 * const desktopWidth = (1200, 'Default', true, false)
 * // Returns calculated width based on available space and column count
 *
 * // Mobile layout
 * const mobileWidth = getExplorerPaneWidth(768, 'Default', false, true)
 * // Returns full explorer width for mobile (768px window)
 * ```
 */

export function getAdaptedBaseColumnWidth(
  columnSetting: ColumnSetting,
  windowWidth: number,
): number {
  let baseWidth = getBaseColumnWidth(columnSetting)
  //  Scale up base width for larger screens
  if (windowWidth >= 1400) baseWidth += 50
  if (windowWidth >= 1600) baseWidth += 100
  if (windowWidth >= 1800) baseWidth += 125

  return baseWidth
}

export function getMaxMinPaneWidth(windowWidth: number): {
  maxPaneWidth: number
  minPaneWidth: number
} {
  let maxPaneWidth = MAXIMUM_PANE_WIDTH
  let minPaneWidth = MINIMUM_PANE_WIDTH

  if (windowWidth >= 600) minPaneWidth = 300
  if (windowWidth > 1800) {
    minPaneWidth += 150
    maxPaneWidth += 100
  }

  return {
    maxPaneWidth,
    minPaneWidth,
  }
}

export function getExplorerPaneWidth(
  windowWidth: number,
  columnSetting: ColumnSetting | null | undefined = 'Default',
  sidebarOpen: boolean,
  shortlistsOpen: boolean = true,
  launchedOnMobileDevice: boolean,
  playlistCreatorWidth: number = 400,
  precalculatedExplorerWidth?: number,
): number {
  // Calculate the available width for the entire explorer
  const explorerWidth =
    precalculatedExplorerWidth ??
    getExplorerWidth(
      windowWidth,
      sidebarOpen,
      shortlistsOpen, // Always calculate as if shortlists are open to prevent pane width changes
      launchedOnMobileDevice,
      playlistCreatorWidth,
    )

  if (launchedOnMobileDevice && windowWidth < 600) {
    // For mobile under 600px, subtract the 10px margin-right on the ExplorerPane
    return explorerWidth - EXPLORER_RIGHT_SPACER
  } else {
    const { maxPaneWidth, minPaneWidth } = getMaxMinPaneWidth(windowWidth)
    // For desktop, calculate column-based width
    const baseWidth = getAdaptedBaseColumnWidth(
      columnSetting || 'Default',
      windowWidth,
    )

    let availableWidthForColumns = explorerWidth
    if (windowWidth >= 1200) availableWidthForColumns += playlistCreatorWidth

    // Calculate how many columns can fit based on base width
    const colCount = getConstrainedColCount(
      explorerWidth,
      columnSetting || 'Default',
    )
    // Calculate the column width by dividing available space by column count
    const calculatedColWidth = Math.floor(availableWidthForColumns / colCount)
    const colWidth = Math.min(baseWidth, calculatedColWidth)

    // subtract the padding space when Playlist CREATOR is visible
    let paneWidth = colWidth - PANE_SPACING
    if (paneWidth < minPaneWidth) paneWidth = minPaneWidth

    if (windowWidth > 1800) {
      paneWidth = Math.floor(colWidth + 100)
    }
    // Ensure width is within reasonable bounds
    if (paneWidth < minPaneWidth) return minPaneWidth
    if (paneWidth > maxPaneWidth) return maxPaneWidth
    return paneWidth
  }
}

/**
 * Calculates the width style for search items.
 *
 * Determines the appropriate width for search result items based on
 * the current window width, accounting for margins and padding.
 *
 * @returns Style object with width property
 *
 * @example
 * ```typescript
 * const style = getSearchItemWidthStyle()
 * // { width: '1175px' } - for a 1200px window minus 24.5rem margins
 *
 * // Apply to component
 * <div style={getSearchItemWidthStyle()}>Search results</div>
 * ```
 */
export function getSearchItemWidthStyle(): {
  width: string
} {
  const windowWidth = window.innerWidth
  const width = windowWidth - convertRemToPx(SEARCH_MARGIN_REM)
  return { width: `${width}px` }
}

/**
 * Calculates optimal video dimensions for different contexts.
 *
 * Determines video player size based on window dimensions, aspect ratio,
 * and whether a bigger picture mode is requested. Handles both landscape
 * and portrait orientations with appropriate size constraints.
 *
 * @param biggerPicture - Whether to use bigger picture mode (default: false)
 * @returns Object containing width and height in pixels
 *
 * @example
 * ```typescript
 * // Standard video size
 * const { width, height } = getVideoSize()
 * // Returns size based on window dimensions and orientation
 *
 * // Bigger picture mode
 * const { width, height } = getVideoSize(true)
 * // Returns larger size optimized for full-screen viewing
 *
 * // Apply to video element
 * <video width={width} height={height} />
 * ```
 */
/**
 * Calculates the left offset for the Explorer when launched on mobile devices.
 *
 * Determines how much space to account for on the left side of the Explorer
 * based on screen size and mobile device layout.
 *
 * @param windowWidth - Total window width in pixels
 * @param launchedOnMobileDevice - Whether the app is running on a mobile device
 * @returns Left offset in pixels
 *
 * @example
 * ```typescript
 * // Above 600px launchedOnMobile (tablet) - account for collapsed sidebar
 * const offset = getExplorerLeftOffset(800, true) // Returns 56
 *
 * // Below 600px launchedOnMobile - no sidebar
 * const offset = getExplorerLeftOffset(400, true) // Returns 0
 * ```
 */
export function getExplorerLeftOffset(
  windowWidth: number,
  launchedOnMobileDevice: boolean,
): number {
  if (!launchedOnMobileDevice) {
    return 0 // Desktop positioning handled by CSS
  }

  if (windowWidth >= 600) {
    // Above 600px on mobile: account for collapsed sidebar
    return 56
  }

  // Below 600px on mobile: no sidebar offset needed
  return 0
}

export function getVideoSize(biggerPicture: boolean = false): {
  width: number
  height: number
} {
  let width
  let height
  if (
    biggerPicture ||
    window.innerWidth < MOBILE_BREAKPOINT ||
    window.innerHeight < MOBILE_HEIGHT_BREAKPOINT
  ) {
    if (window.innerWidth > window.innerHeight) {
      // try to get maximum in embeds
      height = window.innerHeight
      width = height / VIDEO_ASPECT_RATIO
      // nope, use window width instead
      if (width > window.innerWidth) {
        width = window.innerWidth - convertRemToPx(VIDEO_MARGIN_REM)
        height = width * VIDEO_ASPECT_RATIO
      }

      if (width > convertRemToPx(VIDEO_MAX_WIDTH_REM)) {
        width = convertRemToPx(VIDEO_MAX_WIDTH_REM)
        height = width * VIDEO_ASPECT_RATIO
      }
    } else {
      width = window.innerWidth - convertRemToPx(VIDEO_MARGIN_REM)
      height = width * VIDEO_ASPECT_RATIO
    }
  } else {
    width = window.innerWidth / 3 > 430 ? window.innerWidth / 3 : 430
    height = width * VIDEO_ASPECT_RATIO
  }

  return { width, height }
}
